import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Server, 
  Circle,
  Clock,
  Wifi,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MikrotikStatusWidget = () => {
  const [routers, setRouters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initialDelay = setTimeout(() => {
      fetchRouterStatus();
    }, 1000);
    
    const interval = setInterval(fetchRouterStatus, 300000);
    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, []);

  const fetchRouterStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/mpanel/available-routers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRouters(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching router status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-slate-200 dark:border-slate-700 shadow-lg bg-white dark:bg-slate-800/60 dark:backdrop-blur-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (routers.length === 0) {
    return (
      <Card className="border border-slate-200 dark:border-slate-700 shadow-lg bg-white dark:bg-slate-800/60 dark:backdrop-blur-sm">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-2 rounded-lg shadow-md">
              <Server className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Status Router MikroTik</h3>
          </div>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertCircle className="w-12 h-12 text-slate-400 dark:text-slate-500 mb-2" />
            <p className="text-sm text-slate-500 dark:text-slate-400">Tidak ada router yang tersedia</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-slate-200 dark:border-slate-700 shadow-lg bg-white dark:bg-slate-800/60 dark:backdrop-blur-sm overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-2 rounded-lg shadow-lg shadow-cyan-500/30">
              <Server className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Status Router MikroTik</h3>
          </div>
          <Badge className="bg-white dark:bg-slate-800/80 text-cyan-600 dark:text-cyan-400 border-2 border-cyan-300 dark:border-cyan-600 text-xs font-medium">
            {routers.length} Device{routers.length > 1 ? 's' : ''}
          </Badge>
        </div>

        {/* Router List */}
        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
          {routers.map((router, index) => (
            <div 
              key={index}
              className={`flex items-center justify-between p-3.5 bg-white dark:bg-slate-900/40 rounded-xl border-2 transition-all duration-300 ${
                router.is_online 
                  ? 'border-emerald-200 dark:border-emerald-700 hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md' 
                  : 'border-red-200 dark:border-red-700 hover:border-red-400 dark:hover:border-red-500 hover:shadow-md'
              }`}
            >
              {/* Left Side - Status & Info */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Status Indicator */}
                <div className="flex-shrink-0">
                  <div className={`w-3 h-3 rounded-full ${
                    router.is_online 
                      ? 'bg-emerald-500 animate-pulse shadow-lg shadow-emerald-500/50' 
                      : 'bg-red-500 shadow-lg shadow-red-500/50'
                  }`}></div>
                </div>

                {/* Router Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Wifi className={`w-4 h-4 flex-shrink-0 ${router.is_online ? 'text-emerald-600' : 'text-red-500'}`} />
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {router.name || router.router_name || 'Unknown Router'}
                    </h4>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <span className="font-mono">{router.host}</span>
                    {(router.location || router.branch_name) && (
                      <>
                        <span className="text-slate-400 dark:text-slate-500">•</span>
                        <span className={`truncate ${router.is_online ? 'text-emerald-600' : 'text-red-500'}`}>
                          {router.location || router.branch_name}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side - Status Badge */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge 
                  className={`text-xs font-medium bg-white dark:bg-slate-800/80 ${
                    router.is_online
                      ? 'text-emerald-600 dark:text-emerald-400 border-2 border-emerald-300 dark:border-emerald-600' 
                      : 'text-red-600 dark:text-red-400 border-2 border-red-300 dark:border-red-600'
                  }`}
                >
                  <Circle className="w-2 h-2 mr-1 fill-current" />
                  {router.is_online ? 'Online' : 'Offline'}
                </Badge>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Info */}
        <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
              <Clock className="w-3 h-3" />
              <span>Last updated: {new Date().toLocaleTimeString('id-ID')}</span>
            </div>
            <a 
              href="/app/mpanel" 
              className="text-cyan-600 hover:text-cyan-700 font-medium transition-colors"
            >
              Lihat Detail →
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MikrotikStatusWidget;
